import { useState } from "react";
import { trpc } from "@/lib/trpc";
import { useCompany } from "@/contexts/CompanyContext";
import { useAuth } from "@/_core/hooks/useAuth";
import DashboardLayout from "@/components/DashboardLayout";
import CSVTuningPanel from "@/components/CSVTuningPanel";
import {
  BulkDocumentProcessor,
  DocumentCorrelationView,
} from "@/components/DocumentIntelligence";
import { Button } from "@/components/ui/button";
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  CardDescription,
} from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Checkbox } from "@/components/ui/checkbox";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Skeleton } from "@/components/ui/skeleton";
import {
  Brain,
  FileText,
  CheckCircle,
  XCircle,
  Clock,
  ArrowRight,
  Building2,
  Users,
  TrendingUp,
  Check,
  Loader2,
  AlertCircle,
  Trash2,
  Settings2,
  Sparkles,
  ArrowRightLeft,
  BarChart3,
  BookOpen,
} from "lucide-react";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { IntelligenceRefineButton } from "@/components/IntelligenceRefineButton";
import { ConfirmDialog } from "@/components/ui/confirm-dialog";
import { toast } from "sonner";

// Workflow steps
type WorkflowStep = "select" | "tune" | "analyzing";

// Tuning params type (matches router schema)
interface TuningParams {
  documents?: Record<
    string,
    {
      skipRows?: number;
      delimiter?: "," | ";" | "\t" | "|";
      dateColumn?: string;
      amountColumn?: string;
      descriptionColumn?: string;
      vendorHints?: string;
      ignoreColumns?: string[];
      notes?: string;
    }
  >;
  globalHints?: string;
}

// Top-level mode tabs
type IntelligenceMode =
  | "analysis"
  | "processing"
  | "correlation"
  | "patterns"
  | "accuracy";
type AccuracyDays = 30 | 90 | 0;

export default function Intelligence() {
  const { selectedCompanyId } = useCompany();
  const { user } = useAuth();
  const isAdmin = ["admin", "canton_admin", "super_admin"].includes(
    user?.role ?? ""
  );
  const [mode, setMode] = useState<IntelligenceMode>("analysis");
  const [selectedDocs, setSelectedDocs] = useState<number[]>([]);
  const [activeSession, setActiveSession] = useState<number | null>(null);
  const [workflowStep, setWorkflowStep] = useState<WorkflowStep>("select");
  const [deleteSessionId, setDeleteSessionId] = useState<number | null>(null);
  const [accuracyDays, setAccuracyDays] = useState<AccuracyDays>(30);

  // Fetch inbox documents
  const { data: inboxDocs, isLoading: docsLoading } =
    trpc.documentManagement.listByFolder.useQuery(
      {
        companyId: selectedCompanyId!,
        folder: "inbox",
        limit: 100,
      },
      { enabled: !!selectedCompanyId }
    );

  // Fetch sessions
  const { data: sessions, refetch: refetchSessions } =
    trpc.intelligence.listSessions.useQuery(
      {
        companyId: selectedCompanyId!,
        limit: 10,
      },
      { enabled: !!selectedCompanyId }
    );

  // Create session mutation (with tuning support)
  const createSessionMutation =
    trpc.intelligence.createSessionWithTuning.useMutation({
      onSuccess: session => {
        setActiveSession(session.id);
        setSelectedDocs([]);
        setWorkflowStep("select");
        refetchSessions();
      },
    });

  // Delete session mutation
  const deleteSessionMutation = trpc.intelligence.deleteSession.useMutation({
    onSuccess: () => {
      setActiveSession(null);
      refetchSessions();
    },
  });

  // Active session data
  const { data: currentSession, refetch: refetchSession } =
    trpc.intelligence.getSession.useQuery(
      { sessionId: activeSession! },
      { enabled: !!activeSession, refetchInterval: 2000 }
    );

  const { data: sessionStats } = trpc.intelligence.getSessionStats.useQuery(
    { sessionId: activeSession! },
    { enabled: !!activeSession && currentSession?.status !== "analyzing" }
  );

  const { data: vendors, refetch: refetchVendors } =
    trpc.intelligence.getExtractedEntities.useQuery(
      { sessionId: activeSession!, entityType: "vendor" },
      {
        enabled:
          !!activeSession && currentSession?.status === "awaiting_review",
      }
    );

  const { data: clients, refetch: refetchClients } =
    trpc.intelligence.getExtractedEntities.useQuery(
      { sessionId: activeSession!, entityType: "client" },
      {
        enabled:
          !!activeSession && currentSession?.status === "awaiting_review",
      }
    );

  const bulkConfirmMutation = trpc.intelligence.bulkConfirmEntities.useMutation(
    {
      onSuccess: () => refetchSession(),
    }
  );

  const importMutation = trpc.intelligence.importEntities.useMutation({
    onSuccess: () => {
      refetchSession();
      refetchSessions();
      setActiveSession(null);
    },
  });

  // Learned patterns query (gt-c2v22)
  const { data: learnedPatterns } = trpc.intelligence.learnedPatterns.useQuery(
    { companyId: selectedCompanyId!, limit: 10 },
    { enabled: !!selectedCompanyId && mode === "patterns" }
  );

  // Model info query (gt-c3e30)
  const { data: modelInfo } = trpc.intelligence.modelInfo.useQuery();

  // Stale session cleanup mutation (gt-c3o40)
  const cleanupMutation = trpc.intelligence.cleanupStaleSessions.useMutation({
    onSuccess: data => {
      if (data.cleaned === 0) {
        toast.info("No stale sessions found");
      } else {
        toast.success(
          `Cleaned up ${data.cleaned} stale session${data.cleaned !== 1 ? "s" : ""}`
        );
        refetchSessions();
      }
    },
    onError: err => toast.error(`Cleanup failed: ${err.message}`),
  });

  // Categorization accuracy query (gt-c2y25)
  const { data: accuracy } = trpc.intelligence.categorizationAccuracy.useQuery(
    { companyId: selectedCompanyId!, days: accuracyDays },
    { enabled: !!selectedCompanyId && mode === "accuracy" }
  );

  // Check if selected docs include CSVs (show tuning step)
  const hasCSVs = inboxDocs?.some(
    doc =>
      selectedDocs.includes(doc.id) &&
      (doc.mimeType?.includes("csv") || doc.fileName?.endsWith(".csv"))
  );

  // Go to tuning step
  const handleGoToTuning = () => {
    if (selectedDocs.length === 0 || !selectedCompanyId) return;
    setWorkflowStep("tune");
  };

  // Start analysis with tuning params
  const handleStartAnalysisWithTuning = (tuningParams: TuningParams) => {
    if (selectedDocs.length === 0 || !selectedCompanyId) return;

    createSessionMutation.mutate({
      companyId: selectedCompanyId,
      documentIds: selectedDocs,
      name: `Batch Analysis - ${new Date().toLocaleDateString()}`,
      tuningParams,
    });
  };

  // Start analysis without tuning (legacy)
  const handleStartAnalysis = () => {
    if (selectedDocs.length === 0 || !selectedCompanyId) return;

    createSessionMutation.mutate({
      companyId: selectedCompanyId,
      documentIds: selectedDocs,
      name: `Batch Analysis - ${new Date().toLocaleDateString()}`,
    });
  };

  const handleConfirmHighConfidence = () => {
    if (!vendors) return;
    const highConfidenceIds = vendors
      .filter(v => v.confidence >= 80 && v.status === "pending")
      .map(v => v.id);

    if (highConfidenceIds.length > 0) {
      bulkConfirmMutation.mutate({ entityIds: highConfidenceIds });
    }
  };

  const handleImport = () => {
    if (!activeSession) return;
    importMutation.mutate({ sessionId: activeSession });
  };

  // Active session workspace view
  if (activeSession && currentSession) {
    return (
      <DashboardLayout>
        <div className="space-y-6">
          {/* Header */}
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
            <div>
              <h1 className="text-2xl sm:text-3xl font-bold">
                Intelligence Workspace
              </h1>
              <p className="text-sm text-muted-foreground mt-1">
                {currentSession.name || `Session #${currentSession.id}`}
              </p>
            </div>
            <Button variant="outline" onClick={() => setActiveSession(null)}>
              Back to Sessions
            </Button>
          </div>

          {/* Status Banner */}
          <Card>
            <CardContent className="pt-6">
              {currentSession.status === "analyzing" && (
                <div className="flex flex-col sm:flex-row items-start sm:items-center gap-4">
                  <Loader2 className="h-8 w-8 animate-spin text-[var(--primary)] shrink-0" />
                  <div className="flex-1">
                    <h3 className="font-semibold text-lg">
                      Analyzing Documents
                    </h3>
                    <p className="text-sm text-muted-foreground">
                      Processing {currentSession.documentCount} documents with
                      holistic batch intelligence
                    </p>
                  </div>
                </div>
              )}

              {currentSession.status === "awaiting_review" && (
                <div className="flex flex-col gap-4">
                  <div className="flex items-start gap-4">
                    <Brain className="h-8 w-8 text-[var(--success)] shrink-0" />
                    <div className="flex-1">
                      <h3 className="font-semibold text-lg">
                        Analysis Complete
                      </h3>
                      <p className="text-sm text-muted-foreground">
                        Found {currentSession.vendorsExtracted} vendors,{" "}
                        {currentSession.clientsExtracted} clients from{" "}
                        {currentSession.totalTransactions} transactions
                      </p>
                    </div>
                  </div>
                  <div className="flex flex-wrap gap-2">
                    <Button
                      onClick={handleConfirmHighConfidence}
                      disabled={bulkConfirmMutation.isPending}
                      size="sm"
                    >
                      {bulkConfirmMutation.isPending ? (
                        <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                      ) : (
                        <Check className="h-4 w-4 mr-2" />
                      )}
                      Confirm High Confidence
                    </Button>
                    <Button
                      onClick={handleImport}
                      disabled={importMutation.isPending}
                      size="sm"
                      variant="default"
                    >
                      {importMutation.isPending ? (
                        <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                      ) : (
                        <ArrowRight className="h-4 w-4 mr-2" />
                      )}
                      Import to Database
                    </Button>
                  </div>
                </div>
              )}

              {currentSession.status === "completed" && (
                <div className="flex items-center gap-4">
                  <CheckCircle className="h-8 w-8 text-[var(--success)] shrink-0" />
                  <div>
                    <h3 className="font-semibold text-lg">Import Complete</h3>
                    <p className="text-sm text-muted-foreground">
                      Successfully imported entities from intelligence analysis
                    </p>
                  </div>
                </div>
              )}

              {currentSession.status === "failed" && (
                <div className="flex items-start gap-4">
                  <AlertCircle className="h-8 w-8 text-destructive shrink-0" />
                  <div>
                    <h3 className="font-semibold text-lg">Analysis Failed</h3>
                    <p className="text-sm text-muted-foreground">
                      {currentSession.errorMessage || "Unknown error occurred"}
                    </p>
                  </div>
                </div>
              )}
            </CardContent>
          </Card>

          {/* Validation Report - shown when analysis is complete and report exists */}
          {currentSession.status === "awaiting_review" &&
            (currentSession.validationReport ||
              currentSession.errorMessage) && (
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <FileText className="h-5 w-5" />
                    Validation Report
                  </CardTitle>
                  <CardDescription>
                    AI-generated analysis of document quality and data patterns
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="bg-muted/50 rounded-lg p-4 max-h-96 overflow-y-auto">
                    <pre className="whitespace-pre-wrap text-sm font-mono leading-relaxed">
                      {currentSession.validationReport ??
                        currentSession.errorMessage}
                    </pre>
                  </div>
                </CardContent>
              </Card>
            )}

          {/* Statistics */}
          {sessionStats && currentSession.status === "awaiting_review" && (
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <Card>
                <CardHeader className="pb-3">
                  <CardTitle className="text-sm font-medium">
                    Confidence Breakdown
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="space-y-3">
                    <div className="flex justify-between items-center">
                      <span className="text-sm text-muted-foreground">
                        High (80-100%)
                      </span>
                      <Badge variant="default">
                        {sessionStats.byConfidence.high}
                      </Badge>
                    </div>
                    <div className="flex justify-between items-center">
                      <span className="text-sm text-muted-foreground">
                        Medium (50-79%)
                      </span>
                      <Badge variant="secondary">
                        {sessionStats.byConfidence.medium}
                      </Badge>
                    </div>
                    <div className="flex justify-between items-center">
                      <span className="text-sm text-muted-foreground">
                        Low (&lt;50%)
                      </span>
                      <Badge variant="outline">
                        {sessionStats.byConfidence.low}
                      </Badge>
                    </div>
                  </div>
                </CardContent>
              </Card>

              <Card>
                <CardHeader className="pb-3">
                  <CardTitle className="text-sm font-medium">
                    Entity Types
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="space-y-3">
                    <div className="flex justify-between items-center">
                      <span className="text-sm text-muted-foreground flex items-center gap-2">
                        <Building2 className="h-4 w-4" /> Vendors
                      </span>
                      <Badge>{sessionStats.byType.vendor}</Badge>
                    </div>
                    <div className="flex justify-between items-center">
                      <span className="text-sm text-muted-foreground flex items-center gap-2">
                        <Users className="h-4 w-4" /> Clients
                      </span>
                      <Badge>{sessionStats.byType.client}</Badge>
                    </div>
                    <div className="flex justify-between items-center">
                      <span className="text-sm text-muted-foreground flex items-center gap-2">
                        <TrendingUp className="h-4 w-4" /> Patterns
                      </span>
                      <Badge>{sessionStats.byType.pattern}</Badge>
                    </div>
                  </div>
                </CardContent>
              </Card>

              <Card>
                <CardHeader className="pb-3">
                  <CardTitle className="text-sm font-medium">
                    Review Status
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="space-y-3">
                    <div className="flex justify-between items-center">
                      <span className="text-sm text-muted-foreground">
                        Pending
                      </span>
                      <Badge variant="outline">
                        {sessionStats.byStatus.pending}
                      </Badge>
                    </div>
                    <div className="flex justify-between items-center">
                      <span className="text-sm text-muted-foreground">
                        Confirmed
                      </span>
                      <Badge variant="default">
                        {sessionStats.byStatus.confirmed}
                      </Badge>
                    </div>
                    <div className="flex justify-between items-center">
                      <span className="text-sm text-muted-foreground">
                        Imported
                      </span>
                      <Badge className="bg-[var(--success)] text-white">
                        {sessionStats.byStatus.imported}
                      </Badge>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </div>
          )}

          {/* Entity Review Tabs */}
          {currentSession.status === "awaiting_review" && (
            <Tabs defaultValue="vendors" className="w-full">
              <div className="flex items-center justify-between">
                <TabsList>
                  <TabsTrigger value="vendors">
                    Vendors ({vendors?.length || 0})
                  </TabsTrigger>
                  <TabsTrigger value="clients">
                    Clients ({clients?.length || 0})
                  </TabsTrigger>
                </TabsList>
                <div className="flex gap-2">
                  <IntelligenceRefineButton
                    entityType="vendor"
                    sessionId={activeSession}
                    onRefineComplete={() => refetchVendors()}
                  />
                  <IntelligenceRefineButton
                    entityType="client"
                    sessionId={activeSession}
                    onRefineComplete={() => refetchClients()}
                  />
                </div>
              </div>

              <TabsContent value="vendors" className="mt-6 space-y-4">
                {!vendors ? (
                  <div className="space-y-4">
                    {[1, 2, 3].map(i => (
                      <Card key={i}>
                        <CardHeader>
                          <Skeleton className="h-6 w-48" />
                          <Skeleton className="h-4 w-64 mt-2" />
                        </CardHeader>
                      </Card>
                    ))}
                  </div>
                ) : vendors.length === 0 ? (
                  <Card>
                    <CardContent className="py-12">
                      <div className="text-center">
                        <Building2 className="h-12 w-12 mx-auto mb-4 text-muted-foreground" />
                        <p className="text-sm text-muted-foreground">
                          No vendors extracted
                        </p>
                      </div>
                    </CardContent>
                  </Card>
                ) : (
                  vendors.map(vendor => (
                    <Card key={vendor.id}>
                      <CardHeader>
                        <div className="flex flex-col sm:flex-row sm:items-start justify-between gap-3">
                          <div className="flex-1 min-w-0">
                            <CardTitle className="flex flex-wrap items-center gap-2 text-lg">
                              <span className="truncate">{vendor.name}</span>
                              {vendor.swissUid && (
                                <Badge
                                  variant="outline"
                                  className="font-mono text-xs shrink-0"
                                >
                                  {vendor.swissUid}
                                </Badge>
                              )}
                            </CardTitle>
                            <CardDescription className="mt-1">
                              {vendor.frequency} transactions • CHF{" "}
                              {(
                                (vendor.totalAmount || 0) / 100
                              ).toLocaleString()}{" "}
                              total
                            </CardDescription>
                          </div>
                          <div className="flex flex-wrap items-center gap-2 shrink-0">
                            <Badge
                              variant={
                                vendor.confidence >= 80
                                  ? "default"
                                  : vendor.confidence >= 50
                                    ? "secondary"
                                    : "outline"
                              }
                            >
                              {vendor.confidence}% confidence
                            </Badge>
                            {vendor.status === "pending" && (
                              <Badge variant="outline">Pending</Badge>
                            )}
                            {vendor.status === "confirmed" && (
                              <Badge className="bg-[var(--success)] text-white">
                                Confirmed
                              </Badge>
                            )}
                            {vendor.status === "imported" && (
                              <Badge className="bg-[var(--info)] text-white">
                                Imported
                              </Badge>
                            )}
                          </div>
                        </div>
                      </CardHeader>
                      <CardContent>
                        <div className="space-y-3 text-sm">
                          {vendor.category && (
                            <div className="flex gap-2">
                              <span className="font-medium text-muted-foreground">
                                Category:
                              </span>
                              <span>{vendor.category}</span>
                            </div>
                          )}
                          {vendor.suggestedMwstRate && (
                            <div className="flex gap-2">
                              <span className="font-medium text-muted-foreground">
                                MWST:
                              </span>
                              <span>{vendor.suggestedMwstRate}%</span>
                            </div>
                          )}
                          {vendor.sampleDescriptions &&
                            vendor.sampleDescriptions.length > 0 && (
                              <div>
                                <p className="font-medium text-muted-foreground mb-2">
                                  Sample Transactions:
                                </p>
                                <ul className="space-y-1 text-muted-foreground">
                                  {vendor.sampleDescriptions
                                    .slice(0, 3)
                                    .map((desc, i) => (
                                      <li
                                        key={i}
                                        className="text-xs truncate pl-4 border-l-2 border-border"
                                      >
                                        {desc}
                                      </li>
                                    ))}
                                </ul>
                              </div>
                            )}
                        </div>
                      </CardContent>
                    </Card>
                  ))
                )}
              </TabsContent>

              <TabsContent value="clients" className="mt-6 space-y-4">
                {!clients ? (
                  <div className="space-y-4">
                    {[1, 2, 3].map(i => (
                      <Card key={i}>
                        <CardHeader>
                          <Skeleton className="h-6 w-48" />
                          <Skeleton className="h-4 w-64 mt-2" />
                        </CardHeader>
                      </Card>
                    ))}
                  </div>
                ) : clients.length === 0 ? (
                  <Card>
                    <CardContent className="py-12">
                      <div className="text-center">
                        <Users className="h-12 w-12 mx-auto mb-4 text-muted-foreground" />
                        <p className="text-sm text-muted-foreground">
                          No clients extracted
                        </p>
                      </div>
                    </CardContent>
                  </Card>
                ) : (
                  clients.map(client => (
                    <Card key={client.id}>
                      <CardHeader>
                        <div className="flex flex-col sm:flex-row sm:items-start justify-between gap-3">
                          <div className="flex-1 min-w-0">
                            <CardTitle className="text-lg truncate">
                              {client.name}
                            </CardTitle>
                            <CardDescription className="mt-1">
                              {client.frequency} payments • CHF{" "}
                              {(
                                (client.totalAmount || 0) / 100
                              ).toLocaleString()}{" "}
                              total
                            </CardDescription>
                          </div>
                          <Badge
                            variant={
                              client.confidence >= 80 ? "default" : "secondary"
                            }
                            className="shrink-0"
                          >
                            {client.confidence}% confidence
                          </Badge>
                        </div>
                      </CardHeader>
                    </Card>
                  ))
                )}
              </TabsContent>
            </Tabs>
          )}
        </div>
      </DashboardLayout>
    );
  }

  // Guard: Require company selection
  if (!selectedCompanyId) {
    return (
      <DashboardLayout>
        <div className="flex items-center justify-center h-[50vh]">
          <Card className="w-full max-w-md">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Building2 className="h-5 w-5" />
                No Company Selected
              </CardTitle>
              <CardDescription>
                Please select a company from the navigation to use Intelligence
                Workspace
              </CardDescription>
            </CardHeader>
          </Card>
        </div>
      </DashboardLayout>
    );
  }

  // Step 2: Tuning view (CSV preview and configuration)
  if (workflowStep === "tune" && selectedDocs.length > 0) {
    return (
      <DashboardLayout>
        <div className="space-y-6">
          {/* Header */}
          <div>
            <div className="flex items-center gap-3">
              <Brain className="h-8 w-8 text-[var(--primary)]" />
              <h1 className="text-2xl sm:text-3xl font-bold">
                Intelligence Workspace
              </h1>
            </div>
            <p className="text-sm text-muted-foreground mt-2">
              Step 2: Configure CSV parsing before analysis
            </p>
          </div>

          {/* Tuning Panel */}
          <CSVTuningPanel
            documentIds={selectedDocs.filter(id => {
              const doc = inboxDocs?.find(d => d.id === id);
              return (
                doc &&
                (doc.mimeType?.includes("csv") ||
                  doc.fileName?.endsWith(".csv"))
              );
            })}
            companyId={selectedCompanyId}
            onBack={() => setWorkflowStep("select")}
            onStartAnalysis={handleStartAnalysisWithTuning}
            isStarting={createSessionMutation.isPending}
          />
        </div>
      </DashboardLayout>
    );
  }

  // Default view: Mode tabs with session list and document selection
  return (
    <DashboardLayout>
      <div className="space-y-6">
        {/* Header */}
        <div>
          <div className="flex items-center gap-3">
            <Brain className="h-8 w-8 text-[var(--primary)]" />
            <h1 className="text-2xl sm:text-3xl font-bold">
              Intelligence Workspace
            </h1>
          </div>
          <p className="text-sm text-muted-foreground mt-2">
            AI-powered document analysis, processing, and correlation
            {modelInfo && (
              <span className="ml-2 text-xs opacity-70">
                · {modelInfo.modelName}
              </span>
            )}
          </p>
        </div>

        {/* Mode Tabs */}
        <Tabs value={mode} onValueChange={v => setMode(v as IntelligenceMode)}>
          <TabsList className="flex w-full overflow-x-auto justify-start">
            <TabsTrigger
              value="analysis"
              className="flex items-center gap-2 shrink-0"
            >
              <Brain className="h-4 w-4" />
              <span className="hidden sm:inline">Batch Analysis</span>
              <span className="sm:hidden">Analysis</span>
            </TabsTrigger>
            <TabsTrigger
              value="processing"
              className="flex items-center gap-2 shrink-0"
            >
              <Sparkles className="h-4 w-4" />
              <span className="hidden sm:inline">Document Processing</span>
              <span className="sm:hidden">Processing</span>
            </TabsTrigger>
            <TabsTrigger
              value="correlation"
              className="flex items-center gap-2 shrink-0"
            >
              <ArrowRightLeft className="h-4 w-4" />
              <span className="hidden sm:inline">Correlations</span>
              <span className="sm:hidden">Match</span>
            </TabsTrigger>
            <TabsTrigger
              value="patterns"
              className="flex items-center gap-2 shrink-0"
            >
              <BookOpen className="h-4 w-4" />
              <span className="hidden sm:inline">Learned Patterns</span>
              <span className="sm:hidden">Patterns</span>
            </TabsTrigger>
            <TabsTrigger
              value="accuracy"
              className="flex items-center gap-2 shrink-0"
            >
              <BarChart3 className="h-4 w-4" />
              <span className="hidden sm:inline">Accuracy</span>
              <span className="sm:hidden">Accuracy</span>
            </TabsTrigger>
          </TabsList>

          {/* Batch Analysis Tab */}
          <TabsContent value="analysis" className="mt-6 space-y-6">
            {/* Document Selection */}
            <Card>
              <CardHeader>
                <CardTitle>Select Documents for Analysis</CardTitle>
                <CardDescription>
                  Choose CSV or PDF documents from inbox to analyze together
                </CardDescription>
              </CardHeader>
              <CardContent>
                {docsLoading ? (
                  <div className="space-y-3">
                    {[1, 2, 3].map(i => (
                      <div
                        key={i}
                        className="flex items-center gap-3 p-3 border rounded-lg"
                      >
                        <Skeleton className="h-4 w-4" />
                        <div className="flex-1">
                          <Skeleton className="h-4 w-48" />
                          <Skeleton className="h-3 w-32 mt-2" />
                        </div>
                      </div>
                    ))}
                  </div>
                ) : !inboxDocs || inboxDocs.length === 0 ? (
                  <div className="text-center py-12">
                    <FileText className="h-12 w-12 mx-auto mb-4 text-muted-foreground" />
                    <p className="text-sm font-medium text-muted-foreground">
                      No documents in inbox
                    </p>
                    <p className="text-xs text-muted-foreground mt-2">
                      Upload bank statements and credit card CSVs to get started
                    </p>
                  </div>
                ) : (
                  <div className="space-y-4">
                    <div className="space-y-2">
                      {inboxDocs.map(doc => (
                        <div
                          key={doc.id}
                          className="flex items-center gap-3 p-3 border rounded-lg hover:bg-accent/50 transition-colors"
                        >
                          <Checkbox
                            checked={selectedDocs.includes(doc.id)}
                            onCheckedChange={checked => {
                              if (checked) {
                                setSelectedDocs([...selectedDocs, doc.id]);
                              } else {
                                setSelectedDocs(
                                  selectedDocs.filter(id => id !== doc.id)
                                );
                              }
                            }}
                          />
                          <div className="flex-1 min-w-0">
                            <p className="font-medium text-sm truncate">
                              {doc.originalName || doc.fileName}
                            </p>
                            <p className="text-xs text-muted-foreground">
                              {doc.documentType} •{" "}
                              {Math.round(doc.fileSize / 1024)} KB
                            </p>
                          </div>
                        </div>
                      ))}
                    </div>

                    <div className="pt-2 flex flex-col sm:flex-row justify-between items-start sm:items-center gap-3 border-t">
                      <p className="text-sm text-muted-foreground">
                        {selectedDocs.length}{" "}
                        {selectedDocs.length === 1 ? "document" : "documents"}{" "}
                        selected
                        {hasCSVs && selectedDocs.length > 0 && (
                          <span className="ml-2 text-[var(--primary)]">
                            (CSV tuning available)
                          </span>
                        )}
                      </p>
                      <div className="flex gap-2">
                        {hasCSVs && (
                          <Button
                            variant="outline"
                            onClick={handleGoToTuning}
                            disabled={
                              selectedDocs.length === 0 ||
                              createSessionMutation.isPending
                            }
                          >
                            <Settings2 className="h-4 w-4 mr-2" />
                            Configure CSVs
                          </Button>
                        )}
                        <Button
                          onClick={handleStartAnalysis}
                          disabled={
                            selectedDocs.length === 0 ||
                            createSessionMutation.isPending
                          }
                        >
                          {createSessionMutation.isPending ? (
                            <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                          ) : (
                            <Brain className="h-4 w-4 mr-2" />
                          )}
                          {hasCSVs ? "Quick Analyze" : "Start Analysis"}
                          <ArrowRight className="h-4 w-4 ml-2" />
                        </Button>
                      </div>
                    </div>
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Recent Sessions */}
            {sessions && sessions.length > 0 && (
              <Card>
                <CardHeader>
                  <div className="flex items-center justify-between">
                    <div>
                      <CardTitle>Recent Analysis Sessions</CardTitle>
                      <CardDescription>
                        Click a session to view results
                      </CardDescription>
                    </div>
                    {isAdmin && (
                      <Button
                        variant="outline"
                        size="sm"
                        className="shrink-0 text-xs"
                        disabled={cleanupMutation.isPending}
                        onClick={() => cleanupMutation.mutate()}
                      >
                        {cleanupMutation.isPending ? (
                          <Loader2 className="h-3.5 w-3.5 mr-1.5 animate-spin" />
                        ) : (
                          <Trash2 className="h-3.5 w-3.5 mr-1.5" />
                        )}
                        Clean up stale
                      </Button>
                    )}
                  </div>
                </CardHeader>
                <CardContent>
                  <div className="space-y-2">
                    {sessions.map(session => (
                      <div
                        key={session.id}
                        className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 p-3 border rounded-lg hover:bg-accent/50 transition-colors"
                      >
                        <div
                          className="flex-1 min-w-0 cursor-pointer"
                          onClick={() => setActiveSession(session.id)}
                        >
                          <p className="font-medium text-sm truncate">
                            {session.name || `Session #${session.id}`}
                          </p>
                          <p className="text-xs text-muted-foreground">
                            {session.documentCount} documents •{" "}
                            {session.vendorsExtracted} vendors •{" "}
                            {session.clientsExtracted} clients
                          </p>
                        </div>
                        <div className="flex items-center gap-2">
                          <Badge
                            variant={
                              session.status === "completed"
                                ? "default"
                                : session.status === "awaiting_review"
                                  ? "secondary"
                                  : session.status === "failed"
                                    ? "destructive"
                                    : "outline"
                            }
                            className="shrink-0"
                          >
                            {session.status.replace("_", " ")}
                          </Badge>
                          <Button
                            variant="ghost"
                            size="icon"
                            className="h-8 w-8 text-muted-foreground hover:text-destructive"
                            onClick={e => {
                              e.stopPropagation();
                              setDeleteSessionId(session.id);
                            }}
                            disabled={deleteSessionMutation.isPending}
                          >
                            <Trash2 className="h-4 w-4" />
                          </Button>
                        </div>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>
            )}
          </TabsContent>

          {/* Document Processing Tab */}
          <TabsContent value="processing" className="mt-6">
            <BulkDocumentProcessor
              onProcessingComplete={() => {
                // Optionally switch to correlation tab or refresh data
              }}
            />
          </TabsContent>

          {/* Correlations Tab */}
          <TabsContent value="correlation" className="mt-6">
            <DocumentCorrelationView />
          </TabsContent>

          {/* Learned Patterns Tab (gt-c2v22) */}
          <TabsContent value="patterns" className="mt-6">
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <BookOpen className="h-5 w-5 text-[var(--primary)]" />
                  Learned Patterns
                </CardTitle>
                <CardDescription>
                  Top vendor → account code mappings learned from AI-categorized
                  transactions
                </CardDescription>
              </CardHeader>
              <CardContent>
                {!learnedPatterns ? (
                  <div className="space-y-2">
                    {[1, 2, 3].map(i => (
                      <Skeleton key={i} className="h-10 w-full" />
                    ))}
                  </div>
                ) : learnedPatterns.insufficient ? (
                  <div className="py-10 text-center text-muted-foreground text-sm">
                    Not enough data yet — process at least 5 transactions with
                    AI categorization.
                  </div>
                ) : learnedPatterns.patterns.length === 0 ? (
                  <div className="py-10 text-center text-muted-foreground text-sm">
                    No patterns found yet.
                  </div>
                ) : (
                  <div className="rounded-md border overflow-hidden">
                    <table className="w-full text-sm">
                      <thead className="bg-muted/50">
                        <tr>
                          <th className="text-left px-4 py-2 font-medium">
                            Vendor
                          </th>
                          <th className="text-left px-4 py-2 font-medium">
                            Account
                          </th>
                          <th className="text-right px-4 py-2 font-medium">
                            Count
                          </th>
                          <th className="text-right px-4 py-2 font-medium">
                            Avg Confidence
                          </th>
                        </tr>
                      </thead>
                      <tbody className="divide-y">
                        {learnedPatterns.patterns.map((p, i) => (
                          <tr key={i} className="hover:bg-muted/30">
                            <td className="px-4 py-2 font-medium capitalize">
                              {p.vendor_key ||
                                p.sample_description.slice(0, 40)}
                            </td>
                            <td className="px-4 py-2 text-muted-foreground">
                              <span className="font-mono text-xs mr-2">
                                {p.accountCode}
                              </span>
                              {p.accountName}
                            </td>
                            <td className="px-4 py-2 text-right">
                              {p.usage_count}
                            </td>
                            <td className="px-4 py-2 text-right">
                              <span
                                className={
                                  p.avg_confidence > 80
                                    ? "text-[var(--success)]"
                                    : p.avg_confidence >= 50
                                      ? "text-[var(--warning)]"
                                      : "text-[var(--destructive)]"
                                }
                              >
                                {p.avg_confidence}%
                              </span>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* Accuracy Dashboard Tab (gt-c2y25) */}
          <TabsContent value="accuracy" className="mt-6 space-y-4">
            <div className="flex items-center justify-between">
              <h2 className="text-lg font-semibold">Categorization Accuracy</h2>
              <Select
                value={String(accuracyDays)}
                onValueChange={v => setAccuracyDays(Number(v) as AccuracyDays)}
              >
                <SelectTrigger className="w-40">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="30">Last 30 days</SelectItem>
                  <SelectItem value="90">Last 90 days</SelectItem>
                  <SelectItem value="0">All time</SelectItem>
                </SelectContent>
              </Select>
            </div>

            {!accuracy ? (
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                {[1, 2, 3].map(i => (
                  <Skeleton key={i} className="h-24 w-full rounded-lg" />
                ))}
              </div>
            ) : accuracy.insufficient ? (
              <Card>
                <CardContent className="py-10 text-center text-muted-foreground text-sm">
                  Not enough data yet — need at least 10 AI-categorized
                  transactions.
                </CardContent>
              </Card>
            ) : (
              <>
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                  <Card>
                    <CardContent className="pt-6 text-center">
                      <div className="text-3xl font-bold text-[var(--success)]">
                        {accuracy.acceptanceRate}%
                      </div>
                      <div className="text-sm text-muted-foreground mt-1">
                        Acceptance Rate
                      </div>
                      <div className="text-xs text-muted-foreground">
                        AI suggestion kept
                      </div>
                    </CardContent>
                  </Card>
                  <Card>
                    <CardContent className="pt-6 text-center">
                      <div className="text-3xl font-bold text-[var(--warning)]">
                        {accuracy.overrideRate}%
                      </div>
                      <div className="text-sm text-muted-foreground mt-1">
                        Override Rate
                      </div>
                      <div className="text-xs text-muted-foreground">
                        User changed suggestion
                      </div>
                    </CardContent>
                  </Card>
                  <Card>
                    <CardContent className="pt-6 text-center">
                      <div className="text-3xl font-bold">
                        {accuracy.totalAi}
                      </div>
                      <div className="text-sm text-muted-foreground mt-1">
                        AI Categorized
                      </div>
                      <div className="text-xs text-muted-foreground">
                        Total transactions
                      </div>
                    </CardContent>
                  </Card>
                </div>

                {accuracy.topOverridden.length > 0 && (
                  <Card>
                    <CardHeader>
                      <CardTitle className="text-base">
                        Top Overridden Account Codes
                      </CardTitle>
                      <CardDescription>
                        Where users most often changed AI suggestions
                      </CardDescription>
                    </CardHeader>
                    <CardContent>
                      <div className="space-y-2">
                        {accuracy.topOverridden.map((item, i) => (
                          <div
                            key={i}
                            className="flex items-center justify-between py-2 border-b last:border-0"
                          >
                            <div>
                              <span className="font-mono text-xs text-muted-foreground mr-2">
                                {item.accountCode}
                              </span>
                              <span className="text-sm">
                                {item.accountName}
                              </span>
                            </div>
                            <Badge variant="outline">
                              {item.override_count}×
                            </Badge>
                          </div>
                        ))}
                      </div>
                    </CardContent>
                  </Card>
                )}
              </>
            )}
          </TabsContent>
        </Tabs>
      </div>
      <ConfirmDialog
        open={deleteSessionId !== null}
        title="Delete Session"
        description="Are you sure you want to delete this analysis session? This action cannot be undone."
        confirmLabel="Delete"
        destructive
        onConfirm={() => {
          if (deleteSessionId !== null) {
            deleteSessionMutation.mutate({ sessionId: deleteSessionId });
          }
          setDeleteSessionId(null);
        }}
        onCancel={() => setDeleteSessionId(null)}
      />
    </DashboardLayout>
  );
}
